import type { EventCalendarRangeDates } from '@repo/hooks/useEventCalendarRangeDates';
import type { MeetingPublic } from '@repo/types/meetings';
import type { Dispatch, SetStateAction } from 'react';

import { useEditMeeting } from '@repo/api/meeting/useEditMeeting';
import { normalizeEventTimeToDay } from '@repo/lib/calendar/normalizeEventTimeToDay';
import { baseErrorMessages } from '@repo/lib/messages/baseErrorMessages';
import { format } from 'date-fns';
import { Calendar, Dot } from 'lucide-react';
import { useMemo } from 'react';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogBody,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/dialog';
import { FeaturedIcon } from '@/components/ui/featured-icon';
import { toasterMessageHandler } from '@/lib/toaster-message-handler';

interface UpdateDateTimeMeetingProps {
  event?: MeetingPublic;
  queryParams?: EventCalendarRangeDates;
  open: boolean;
  onOpenChange: Dispatch<SetStateAction<boolean>>;
}

export function UpdateDateTimeMeeting(updateDatetimeMettingProps: UpdateDateTimeMeetingProps) {
  const { event, queryParams, open, onOpenChange } = updateDatetimeMettingProps;

  const { id, startDate, endDate, name } = event || {};

  const { mutateAsync } = useEditMeeting(queryParams);

  const onClose = () => {
    onOpenChange(false);
  };

  const { adjustedStartDate, adjustedEndDate } = useMemo<{ adjustedStartDate: string | undefined; adjustedEndDate: string | undefined }>(() => {
    return normalizeEventTimeToDay({ startDate, endDate });
  }, [startDate, endDate]);

  const onSubmit = () => {
    if (!id) {
      return;
    }

    mutateAsync({
      id,
      startDate: adjustedStartDate,
      endDate: adjustedEndDate,
    })
      .then(() => {
        onClose();
      })
      .catch((error) => {
        toasterMessageHandler(baseErrorMessages, error);
      });
  };

  return (
    <AlertDialog onOpenChange={onOpenChange} open={open}>
      <AlertDialogContent className={'sm:max-w-96'}>
        <AlertDialogBody>
          <AlertDialogHeader>
            <FeaturedIcon size={'lg'}>
              <Calendar />
            </FeaturedIcon>
            <div className={'flex min-w-0 grow flex-col gap-0.5'}>
              <AlertDialogTitle>Potwierdź zmiany</AlertDialogTitle>
              <AlertDialogDescription className={'text-muted-foreground text-xs'}>Edycja daty i godziny spotkania.</AlertDialogDescription>
            </div>
          </AlertDialogHeader>
          <div className={'flex flex-col gap-3 overflow-auto px-4'}>
            <div className={'flex min-h-9 items-center gap-1.5'}>
              <span className={'text-muted-foreground min-w-14'}>Nazwa: </span>
              <p className="text-sm">{name}</p>
            </div>
            <div className={'flex min-h-9 items-center'}>
              <span className={'text-muted-foreground min-w-14'}>Data: </span>
              <p className={'flex items-center text-sm'}>
                <p className={'flex items-center text-sm'}>
                  {adjustedStartDate ? format(new Date(adjustedStartDate), 'dd MMM yyyy, HH:mm') : ''}
                  <Dot className={'size-4'} />
                  {adjustedEndDate ? format(new Date(adjustedEndDate), 'dd MMM yyyy, HH:mm') : ''}
                </p>
              </p>
            </div>
          </div>
        </AlertDialogBody>
        <AlertDialogFooter>
          <AlertDialogCancel variant={'tertiary'} onClick={onClose}>
            Zamknij
          </AlertDialogCancel>
          <AlertDialogAction onClick={onSubmit}>Potwierdź</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
